/**
 * Shared CV parser — converts raw CV text into a structured array of sections.
 * Used by CVPreview, CVDocument, and the section editor (drag & drop / inline edit).
 */

const SECTION_KEYWORDS = [
  "EXPÉRIENCE", "EXPÉRIENCES", "PROFESSIONAL EXPERIENCE",
  "FORMATION", "EDUCATION",
  "COMPÉTENCES CLÉS", "KEY COMPETENCIES",
  "COMPÉTENCES TECHNIQUES", "COMPÉTENCES", "TECHNICAL SKILLS", "SKILLS",
  "PROFIL", "SUMMARY", "PROFILE",
  "LANGUES", "LANGUAGES",
  "CERTIFICATIONS",
  "PROJETS", "PROJECTS",
];

const SUB_LABEL_PREFIXES = [
  "COMPÉTENCES PROFESSIONNELLES", "PROFESSIONAL SKILLS",
  "TECHNOLOGIES :", "TECHNOLOGIES:", "CONTEXTE :", "CONTEXTE:",
  "CONTEXT :", "CONTEXT:", "RÉALISATIONS CLÉS", "KEY CONTRIBUTIONS",
  "RESPONSABILITÉS CLÉS", "KEY RESPONSIBILITIES",
  "ENVIRONNEMENT TECHNIQUE", "TECHNICAL ENVIRONMENT",
  "RÉSULTAT", "RESULT",
];

export const isContactLine = (line) =>
  line.includes("@") ||
  line.includes("•") ||
  line.includes("http") ||
  /^(my website|mon site|linkedin)/i.test(line.trim()) ||
  /^\+?[\d\s\-().]{7,}$/.test(line.trim());

const isSectionTitle = (trimmed) => {
  const upper = trimmed.toUpperCase();
  const isSubLabel = SUB_LABEL_PREFIXES.some((sl) => upper.startsWith(sl));
  if (isSubLabel) return false;
  return (
    SECTION_KEYWORDS.some((kw) => upper.startsWith(kw)) ||
    (trimmed.length > 3 &&
      trimmed === trimmed.toUpperCase() &&
      !/^[-•*0-9✓]/.test(trimmed))
  );
};

/**
 * Classifies a single body line.
 * Returns { type, text, parts?, boldPart?, regularPart? }
 */
const classifyLine = (trimmed, prevNonEmpty) => {
  if (!trimmed) return { type: "empty" };

  if (isSectionTitle(trimmed)) {
    return { type: "sectionTitle", text: trimmed };
  }

  // Role line: right after an experience header (line with |)
  const isRole =
    prevNonEmpty.includes("|") &&
    !trimmed.includes("|") &&
    !SUB_LABEL_PREFIXES.some((sl) => trimmed.toUpperCase().startsWith(sl)) &&
    !SECTION_KEYWORDS.some((kw) => trimmed.toUpperCase().startsWith(kw)) &&
    !trimmed.startsWith("✓") &&
    !/^[-•*]/.test(trimmed) &&
    !trimmed.startsWith("**");
  if (isRole) return { type: "role", text: trimmed };

  // Experience header with |
  if (trimmed.includes("|")) {
    const parts = trimmed.split("|");
    const titlePart = parts[0].trim().replace(/\*\*/g, "");
    const infoPart = parts.slice(1).join("|").trim().replace(/\*\*/g, "");
    return { type: "experienceHeader", text: trimmed, titlePart, infoPart };
  }

  // Bold lines **...**
  if (trimmed.startsWith("**")) {
    const m = trimmed.match(/^\*\*(.+?)\*\*\s*(.*)/);
    if (m && m[2]) {
      return { type: "boldCategory", text: trimmed, boldPart: m[1], regularPart: m[2] };
    }
    return { type: "boldLine", text: trimmed.replace(/\*\*/g, "") };
  }

  // Checkmarks
  if (trimmed.startsWith("✓")) {
    return { type: "check", text: trimmed.replace(/^✓\s*/, "") };
  }

  // Bullets
  if (/^[-•*]\s/.test(trimmed)) {
    return { type: "bullet", text: trimmed.replace(/^[-•*]\s*/, "") };
  }

  return { type: "text", text: trimmed };
};

/**
 * Parse raw CV text into structured sections.
 *
 * Returns: Array<{
 *   id: string,
 *   type: 'header' | 'section',
 *   title: string,
 *   lines: Array<{ type, text, ... }>,
 *   align: 'center' | 'left'
 * }>
 */
export function parseCVText(content) {
  if (!content) return [];

  const rawLines = content.split("\n");
  const sections = [];
  let currentId = 0;

  // ── Extract header ──────────────────────────────────────────────────
  const headerLines = [];
  let bodyStartIndex = 0;
  let linkedInSeen = false;

  for (let i = 0; i < rawLines.length; i++) {
    const line = rawLines[i].trim();
    if (!line) continue;

    headerLines.push(line);

    if (line.toUpperCase().includes("LINKEDIN")) {
      linkedInSeen = true;
    } else if (linkedInSeen && /^(langues?|languages?)\s*:/i.test(line)) {
      bodyStartIndex = i + 1;
      break;
    } else if (linkedInSeen) {
      headerLines.pop(); // this line belongs to body
      bodyStartIndex = i;
      break;
    }
  }

  // If we never broke out, everything is header (edge case for very short CVs)
  if (bodyStartIndex === 0 && headerLines.length > 0) {
    bodyStartIndex = rawLines.length;
  }

  // Classify header lines
  const headerParsed = headerLines.map((line, idx) => {
    if (idx === 0) return { type: "headerName", text: line };
    if (isContactLine(line)) return { type: "headerContact", text: line };
    return { type: "headerTitle", text: line };
  });

  sections.push({
    id: `section-${currentId++}`,
    type: "header",
    title: "En-tête",
    lines: headerParsed,
    align: "center",
  });

  // ── Extract body sections ──────────────────────────────────────────
  // Collect non-empty body lines
  const bodyLines = [];
  for (let i = bodyStartIndex; i < rawLines.length; i++) {
    bodyLines.push(rawLines[i].trim());
  }

  let currentSection = null;

  const flushSection = () => {
    if (currentSection && currentSection.lines.length > 0) {
      sections.push(currentSection);
    }
  };

  let prevNonEmpty = "";
  for (let i = 0; i < bodyLines.length; i++) {
    const trimmed = bodyLines[i];

    if (isSectionTitle(trimmed)) {
      flushSection();
      currentSection = {
        id: `section-${currentId++}`,
        type: "section",
        title: trimmed,
        lines: [],
        align: "left",
      };
      prevNonEmpty = "";
      continue;
    }

    // If no section opened yet, create an unnamed one
    if (!currentSection) {
      currentSection = {
        id: `section-${currentId++}`,
        type: "section",
        title: "",
        lines: [],
        align: "left",
      };
    }

    const classified = classifyLine(trimmed, prevNonEmpty);
    currentSection.lines.push(classified);
    if (trimmed) prevNonEmpty = trimmed;
  }
  flushSection();

  return sections;
}

/**
 * Reconstruct raw text from structured sections (for export / state sync).
 */
export function sectionsToText(sections) {
  const parts = [];

  for (const section of sections) {
    if (section.type === "header") {
      for (const line of section.lines) {
        parts.push(line.text);
      }
      parts.push(""); // blank separator
    } else {
      if (section.title) {
        parts.push(section.title);
      }
      for (const line of section.lines) {
        if (line.type === "empty") {
          parts.push("");
        } else if (line.type === "check") {
          parts.push(`✓ ${line.text}`);
        } else if (line.type === "bullet") {
          parts.push(`- ${line.text}`);
        } else if (line.type === "boldCategory") {
          parts.push(`**${line.boldPart}** ${line.regularPart}`);
        } else if (line.type === "boldLine") {
          parts.push(`**${line.text}**`);
        } else if (line.type === "experienceHeader") {
          parts.push(line.text);
        } else {
          parts.push(line.text);
        }
      }
      parts.push("");
    }
  }

  return parts.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}
